import type React from 'react';
import {useMemo} from 'react';
import {Alert, Box, Stack, Typography} from '@mui/material';
import type {GridColDef} from '@mui/x-data-grid-premium';
import {useOutletContext} from 'react-router';
import {CustomDataGrid} from '~/components/data-grid/CustomDataGrid';
import {DataGridCsvExportButton} from '~/components/data-grid/DataGridCsvExportButton';
import {formatDate} from '~/components/data-grid/cells/gridCellFormat';
import {centered, daysLeftLine} from '~/components/data-grid/cells/gridCells';
import {useProfileContractsQuery} from '../../hooks/useCategoryProfileQueries';
import type {ContractRow} from '../../types/categoryProfileTypes';
import {ProfileSection} from '../components/ProfileSection';
import type {CategoryProfileOutletContext} from '../profileTypes';

const RENEWAL_WINDOW_DAYS = 90;

/** Contracts tab (spec 4.7): contracts linked to the category's spend, with renewal dates. */
export const ContractsTab: React.FC = () => {
    const {databagId, path, header, money} = useOutletContext<CategoryProfileOutletContext>();
    const {data, isFetching, isError} = useProfileContractsQuery(databagId, path);
    const rows = data ?? [];
    const expiring = rows.filter(r => r.daysToExpiry !== null && r.daysToExpiry >= 0 && r.daysToExpiry <= RENEWAL_WINDOW_DAYS);

    const columns: GridColDef<ContractRow>[] = useMemo(() => [
        {field: 'title', headerName: 'Contract', flex: 1, minWidth: 240, valueGetter: (_v, r) => r.title || `Contract ${r.contractId}`},
        {field: 'type', headerName: 'Type', width: 140},
        {field: 'parentSupplier', headerName: 'Parent supplier', width: 180, valueGetter: (_v, r) => r.parentSupplier || r.supplier},
        {field: 'spendUnderContract', headerName: 'Spend under contract', type: 'number', width: 170,
            valueFormatter: (v: number) => money(v)},
        {field: 'effective', headerName: 'Effective', width: 120, valueFormatter: (v: string | null) => formatDate(v)},
        {field: 'expires', headerName: 'Expires', width: 140,
            renderCell: ({row}) => centered(<>
                <Typography variant="body2">{formatDate(row.expires)}</Typography>{daysLeftLine(row.expires)}
            </>)},
        {field: 'autoRenewal', headerName: 'Renewal', width: 110,
            valueFormatter: (v: boolean | null) => v === null ? '—' : v ? 'Automatic' : 'Manual'},
        {field: 'volumeCommitment', headerName: 'Volume commitment', width: 200},
        {field: 'rebate', headerName: 'Rebate', width: 200},
        {field: 'termination', headerName: 'Termination', width: 220},
    ], [money]);

    if (isError) return <Alert severity="error">Could not load the contracts.</Alert>;
    return (
        <ProfileSection title={`Contracts covering ${header.name}`}>
            <Stack gap={1.5}>
                {expiring.length > 0 && (
                    <Alert severity="warning">
                        {`${expiring.length} agreement${expiring.length === 1 ? '' : 's'} expire within ${RENEWAL_WINDOW_DAYS} days.`}
                    </Alert>
                )}
                <Box sx={{height: 520}}>
                    <CustomDataGrid
                        layoutKey="category-profile-contracts"
                        rows={rows} columns={columns} loading={isFetching} getRowId={r => r.contractId}
                        rowSize="medium" getRowHeight={() => 72}
                        initialState={{
                            pagination: {paginationModel: {pageSize: 10, page: 0}},
                            columns: {columnVisibilityModel: {rebate: false, termination: false}},
                        }}
                        disableRowSelectionOnClick showToolbar
                        toolbarProps={{title: `${rows.length} contracts`,
                            children: <DataGridCsvExportButton fileName={`contracts-${header.name}`}/>}}
                        localeText={{noRowsLabel: 'No contracts are linked to spend in this category yet.'}}
                    />
                </Box>
            </Stack>
        </ProfileSection>
    );
};
